/**
 * 9.3 Reportes de CxC — Motor de filtros + tabla pivote dinámica.
 * Permite elegir filas y columnas (incluyendo subdivisión temporal: semana/quincena/mes).
 * Bloques adicionales: Aging por dimensión, Mayores deudores, Días promedio de pago.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  FunnelIcon, DownloadIcon, TableIcon, ClockCounterClockwiseIcon, TrendUpIcon,
} from "@phosphor-icons/react";

const DIMS = [
  { k: "cliente", l: "Cliente" },
  { k: "rtc", l: "RTC (vendedor)" },
  { k: "region", l: "Región" },
  { k: "linea", l: "Línea / Proveedor" },
  { k: "categoria", l: "Categoría" },
  { k: "item", l: "Ítem" },
  { k: "proyecto", l: "Proyecto" },
  { k: "antiguedad", l: "Antigüedad" },
  { k: "estado", l: "Estado (vence/por vencer)" },
  { k: "termino", l: "Término (Contado/Crédito)" },
  { k: "nivel_precio", l: "Nivel de precio" },
  { k: "canal", l: "Canal (directa/RTC)" },
];

const SUBDIVISIONES = [
  { k: "", l: "Total (sin subdividir)" },
  { k: "semana", l: "Semana" },
  { k: "quincena", l: "Días vencidos por quincena (1–15 … 106–120)" },
  { k: "mes", l: "Días vencidos por mes (1–30 … 91–120)" },
];
const ANTIGUEDAD = ["quincena", "mes"];
const hoyISO = () => new Date().toLocaleDateString("en-CA");

export default function CxCReports() {
  const [filtros, setFiltros] = useState({
    fila: "cliente",
    columna: "quincena",
    valor: "saldo",
    desde: "",
    hasta: hoyISO(),
    region: "",
    rtc: "",
    canal: "",
    nivel_precio: "",
    estado: "",
    linea: "",
    categoria: "",
    item: "",
    termino: "",
    base_fecha: "factura",
    por_vencer_dias: "",
  });
  const [opts, setOpts] = useState({ proveedores: [], categorias: [], items: [], rtcs: [], regiones: [] });
  const [cartera, setCartera] = useState(null);
  const [pivot, setPivot] = useState(null);
  const [aging, setAging] = useState(null);
  const [topDeudores, setTopDeudores] = useState(null);
  const [diasPago, setDiasPago] = useState(null);
  const [agingDim, setAgingDim] = useState("rtc");
  const [diasDim, setDiasDim] = useState("cliente");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/sales/filtros-opciones").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filtros).filter(([_, v]) => v !== "" && v != null));
      const [p, ag, td, dp, cv] = await Promise.all([
        api.get(`/reports/cxc/pivot`, { params }),
        api.get(`/reports/cxc/aging`, { params: { dimension: agingDim, ...Object.fromEntries(Object.entries({ region: filtros.region, rtc: filtros.rtc, linea: filtros.linea, categoria: filtros.categoria, item: filtros.item, termino: filtros.termino, nivel_precio: filtros.nivel_precio }).filter(([, v]) => v)) } }),
        api.get(`/reports/cxc/mayores-deudores`, { params: { limit: 20 } }),
        api.get(`/reports/cxc/dias-promedio-pago`, { params: { dimension: diasDim } }),
        api.get(`/reports/cxc/cartera-vs-limite`).catch(() => ({ data: null })),
      ]);
      setPivot(p.data); setAging(ag.data); setTopDeudores(td.data); setDiasPago(dp.data); setCartera(cv.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtros, agingDim, diasDim]);

  const exportServer = async (format) => {
    try {
      const params = Object.fromEntries(Object.entries({ ...filtros, format }).filter(([, v]) => v !== "" && v != null));
      const res = await api.get(`/reports/cxc/pivot/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cxc_${filtros.fila}_${filtros.columna || "total"}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportPivot = () => {
    if (!pivot) return;
    const cols = pivot.columnas.map((c) => c.label);
    const headers = [pivot.fila_label, ...cols, "Total", "%"];
    const rows = pivot.rows.map((r) => [r.fila, ...cols.map((_, i) => r.celdas[pivot.columnas[i].key] ?? 0), r.total, r.pct]);
    rows.push(["TOTAL", ...pivot.columnas.map((c) => c.total), pivot.grand_total, 100]);
    downloadCSV(`cxc_${filtros.fila}_${filtros.columna || "total"}.csv`, headers, rows);
  };

  return (
    <div className="space-y-5">
      {/* Filtros del motor */}
      <div className="bg-card border border-border/60 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 text-xs text-text-secondary">
          <FunnelIcon size={14} /> Motor de reportes dinámicos
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Field label="Filas">
            <select value={filtros.fila} onChange={(e) => setFiltros({...filtros, fila: e.target.value})} data-testid="cxc-fila" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              {DIMS.map((d) => <option key={d.k} value={d.k}>{d.l}</option>)}
            </select>
          </Field>
          <Field label="Columnas (subdivisión)">
            <select value={filtros.columna} onChange={(e) => setFiltros({...filtros, columna: e.target.value})} data-testid="cxc-columna" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              {SUBDIVISIONES.map((d) => <option key={d.k} value={d.k}>{d.l}</option>)}
              <option disabled>— Por dimensión —</option>
              {DIMS.filter((d) => d.k !== filtros.fila).map((d) => <option key={d.k} value={d.k}>{d.l}</option>)}
            </select>
          </Field>
          <Field label="Valor de celdas">
            <select value={filtros.valor} onChange={(e) => setFiltros({...filtros, valor: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="saldo">Saldo pendiente</option>
              <option value="monto_factura">Total facturado</option>
              <option value="facturas"># Facturas</option>
            </select>
          </Field>
          <Field label="Estado">
            <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todos</option><option value="por_vencer">Por vencer</option><option value="vencido">Vencido</option>
            </select>
          </Field>
          <Field label={ANTIGUEDAD.includes(filtros.columna) ? "Facturas emitidas desde (opcional)" : "Desde"}>
            <input type="date" value={filtros.desde} onChange={(e) => setFiltros({...filtros, desde: e.target.value})} data-testid="cxc-desde" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
          </Field>
          <Field label={ANTIGUEDAD.includes(filtros.columna) ? "FECHA DE CORTE" : "Hasta"}>
            <input type="date" value={filtros.hasta} onChange={(e) => setFiltros({...filtros, hasta: e.target.value})} data-testid="cxc-hasta" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Región">
            <select value={filtros.region} onChange={(e) => setFiltros({...filtros, region: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todas</option>
              {(opts.regiones || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="RTC">
            <select value={filtros.rtc} onChange={(e) => setFiltros({...filtros, rtc: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todos</option>
              {(opts.rtcs || []).map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
            </select>
          </Field>
          <Field label="Línea / proveedor">
            <select value={filtros.linea} onChange={(e) => setFiltros({...filtros, linea: e.target.value})} data-testid="cxc-linea" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todas</option>
              {(opts.proveedores || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Categoría">
            <select value={filtros.categoria} onChange={(e) => setFiltros({...filtros, categoria: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todas</option>
              {(opts.categorias || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Ítem">
            <select value={filtros.item} onChange={(e) => setFiltros({...filtros, item: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todos</option>
              {(opts.items || []).map((i) => <option key={i.id} value={i.id}>{i.codigo} · {i.nombre}</option>)}
            </select>
          </Field>
          <Field label="Término">
            <select value={filtros.termino} onChange={(e) => setFiltros({...filtros, termino: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todos</option><option value="Contado">Contado</option><option value="Crédito">Crédito (todos)</option>
              <option value="Crédito 15d">Crédito 15d</option><option value="Crédito 30d">Crédito 30d</option>
              <option value="Crédito 45d">Crédito 45d</option><option value="Crédito 60d">Crédito 60d</option>
            </select>
          </Field>
          {!ANTIGUEDAD.includes(filtros.columna) && <Field label="Subdividir sobre">
            <select value={filtros.base_fecha} onChange={(e) => setFiltros({...filtros, base_fecha: e.target.value})} data-testid="cxc-base-fecha" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="factura">Fecha de factura</option><option value="vencimiento">Fecha de vencimiento</option>
            </select>
          </Field>}
          <Field label="Próximas a vencer (días)">
            <input type="number" min="0" value={filtros.por_vencer_dias} onChange={(e) => setFiltros({...filtros, por_vencer_dias: e.target.value})} placeholder="—" data-testid="cxc-por-vencer" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Canal">
            <select value={filtros.canal} onChange={(e) => setFiltros({...filtros, canal: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todos</option><option value="directa">Directa</option><option value="rtc">Por RTC</option>
            </select>
          </Field>
          <Field label="Nivel precio">
            <select value={filtros.nivel_precio} onChange={(e) => setFiltros({...filtros, nivel_precio: e.target.value})} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">Todos</option><option value="P0">P0</option><option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option><option value="P4">P4</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Tabla Pivote */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex justify-between items-center">
          <div>
            <h3 className="font-heading text-base flex items-center gap-2"><TableIcon size={16} /> {pivot?.valor_label || "Saldo"} por {pivot?.fila_label} × {pivot?.columna_label}</h3>
            <p className="text-[10px] text-text-muted uppercase tracking-wider mt-0.5">
              {pivot?.modo === "antiguedad"
                ? <>Fecha de corte {fmtDate(pivot.fecha_corte)}{pivot.rango?.desde ? ` · facturas desde ${fmtDate(pivot.rango.desde)}` : ""} · días vencidos según la fecha de vencimiento · saldo neto de abonos y ajustes</>
                : <>{pivot?.rango?.desde} → {pivot?.rango?.hasta}</>}
              {pivot?.grand_total > 0 && ` · Total: ${fmtL(pivot.grand_total)}`}
            </p>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => exportServer("xlsx")} className="text-xs flex items-center gap-1 px-3 py-1.5 border border-border rounded-md hover:border-agro-green hover:text-agro-green" data-testid="cxc-export-xlsx">
              <DownloadIcon size={14} /> Excel
            </button>
            <button onClick={() => exportServer("pdf")} className="text-xs flex items-center gap-1 px-3 py-1.5 border border-border rounded-md hover:border-agro-green hover:text-agro-green" data-testid="cxc-export-pdf">
              <DownloadIcon size={14} /> PDF
            </button>
            <button onClick={exportPivot} className="text-xs flex items-center gap-1 px-3 py-1.5 border border-border rounded-md hover:border-agro-green hover:text-agro-green" data-testid="cxc-export">
              <DownloadIcon size={14} /> CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto max-h-[560px]">
          <table className="w-full text-xs">
            <thead className="bg-sidebar sticky top-0 z-10">
              <tr>
                <th className="p-3 pl-5 text-left text-[10px] uppercase tracking-wider text-text-muted sticky left-0 bg-sidebar z-20">{pivot?.fila_label || "Fila"}</th>
                {pivot?.columnas.map((c) => (
                  <th key={c.key} className="p-3 text-right text-[10px] uppercase tracking-wider text-text-muted whitespace-nowrap">{c.label}</th>
                ))}
                <th className="p-3 pr-5 text-right text-[10px] uppercase tracking-wider text-agro-green sticky right-0 bg-sidebar z-20">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={(pivot?.columnas?.length || 1) + 2} className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : (pivot?.rows || []).length === 0 ? <tr><td colSpan={(pivot?.columnas?.length || 1) + 2} className="p-10 text-center text-text-muted">Sin datos en el rango/filtros</td></tr>
              : pivot.rows.map((r, i) => (
                <tr key={i} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`pivot-row-${i}`}>
                  <td className="p-3 pl-5 sticky left-0 bg-card z-10">{r.fila}</td>
                  {pivot.columnas.map((c) => {
                    const v = r.celdas[c.key] || 0;
                    return (
                      <td key={c.key} className={`p-3 text-right ${v > 0 ? "text-text-primary" : "text-text-muted"}`}>
                        {v > 0 ? fmtL(v) : "—"}
                      </td>
                    );
                  })}
                  <td className="p-3 pr-5 text-right font-medium text-agro-green sticky right-0 bg-card z-10">{fmtL(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {pivot?.rows?.length > 0 && (
              <tfoot className="border-t-2 border-border bg-sidebar/40 sticky bottom-0">
                <tr>
                  <td className="p-3 pl-5 font-semibold uppercase tracking-wider text-[10px] text-text-secondary sticky left-0 bg-sidebar z-10">Total</td>
                  {pivot.columnas.map((c) => (
                    <td key={c.key} className="p-3 text-right font-heading text-sm text-agro-yellow">{fmtL(c.total)}</td>
                  ))}
                  <td className="p-3 pr-5 text-right font-heading text-base text-agro-green sticky right-0 bg-sidebar z-10">{fmtL(pivot.grand_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Aging + Top deudores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60 flex justify-between items-center">
            <h3 className="font-heading text-sm flex items-center gap-2"><ClockCounterClockwiseIcon size={14} /> Antigüedad de saldos</h3>
            <select value={agingDim} onChange={(e) => setAgingDim(e.target.value)} className="text-xs bg-app border border-border rounded-md px-2 py-1">
              {["cliente", "rtc", "region", "linea", "categoria", "item", "proyecto", "termino", "nivel_precio"].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">{agingDim}</th>
                  {(aging?.buckets || []).map((b) => <th key={b} className="p-2 text-right">{b}</th>)}
                  <th className="p-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(aging?.rows || []).slice(0, 30).map((r, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-2 truncate max-w-[140px]">{r.fila}</td>
                    {(aging?.buckets || []).map((b) => (
                      <td key={b} className={`p-2 text-right ${r.buckets[b] > 0 ? "text-text-primary" : "text-text-muted"}`}>
                        {r.buckets[b] > 0 ? fmtL(r.buckets[b]) : "—"}
                      </td>
                    ))}
                    <td className="p-2 text-right font-medium text-agro-green">{fmtL(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              {aging && (
                <tfoot className="bg-sidebar/40 border-t-2 border-border">
                  <tr>
                    <td className="p-2 font-semibold uppercase tracking-wider text-[10px]">Total</td>
                    {aging.buckets.map((b) => (
                      <td key={b} className="p-2 text-right text-agro-yellow font-medium">{fmtL(aging.totales[b] || 0)}</td>
                    ))}
                    <td className="p-2 text-right text-agro-green font-heading">{fmtL(aging.grand_total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60">
            <h3 className="font-heading text-sm flex items-center gap-2"><TrendUpIcon size={14} /> Mayores deudores</h3>
          </div>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">Cliente</th>
                  <th className="p-2 text-right">Saldo</th>
                  <th className="p-2 text-right">Vencido</th>
                  <th className="p-2 text-right">% vencido</th>
                  <th className="p-2 text-right">Límite</th>
                  <th className="p-2 text-right">Uso</th>
                </tr>
              </thead>
              <tbody>
                {(topDeudores?.items || []).map((r, i) => (
                  <tr key={i} className="border-t border-border/30">
                    <td className="p-2 truncate max-w-[170px]">{r.cliente_nombre}</td>
                    <td className="p-2 text-right">{fmtL(r.saldo)}</td>
                    <td className="p-2 text-right text-red-400 font-medium">{fmtL(r.vencido)}</td>
                    <td className={`p-2 text-right font-medium ${r.pct_vencido > 50 ? "text-red-400" : r.pct_vencido > 20 ? "text-agro-yellow" : "text-agro-green"}`}>{r.pct_vencido}%</td>
                    <td className="p-2 text-right text-text-muted">{r.limite_credito ? fmtL(r.limite_credito) : "—"}</td>
                    <td className={`p-2 text-right ${r.sobre_limite ? "text-red-400 font-medium" : ""}`}>{r.uso_limite_pct != null ? `${r.uso_limite_pct}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Cartera vs límite de crédito */}
      {cartera && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="cxc-cartera-limite">
          <div className="p-4 border-b border-border/60 flex justify-between items-center">
            <h3 className="font-heading text-sm">Cartera vs límite de crédito</h3>
            <div className="text-[10px] text-text-muted">{cartera.totales.sobre_limite} de {cartera.totales.clientes} clientes sobre su límite · exceso {fmtL(cartera.totales.exceso)}</div>
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-xs">
              <thead className="bg-sidebar/60 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2">Cliente</th><th className="p-2 text-right">Saldo</th><th className="p-2 text-right">Límite</th>
                  <th className="p-2 text-right">Disponible</th><th className="p-2 text-right">Uso</th><th className="p-2 text-right">Exceso</th>
                </tr>
              </thead>
              <tbody>
                {cartera.rows.map((r, i) => (
                  <tr key={i} className={`border-t border-border/30 ${r.sobre_limite ? "bg-red-500/5" : ""}`}>
                    <td className="p-2">{r.cliente_nombre}</td>
                    <td className="p-2 text-right">{fmtL(r.saldo)}</td>
                    <td className="p-2 text-right text-text-muted">{r.limite ? fmtL(r.limite) : "Sin límite"}</td>
                    <td className="p-2 text-right">{r.disponible != null ? fmtL(r.disponible) : "—"}</td>
                    <td className="p-2 text-right">{r.uso_pct != null ? `${r.uso_pct}%` : "—"}</td>
                    <td className="p-2 text-right text-red-400">{r.exceso > 0 ? fmtL(r.exceso) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Días promedio de pago */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex justify-between items-center">
          <h3 className="font-heading text-sm">Días promedio de pago</h3>
          <select value={diasDim} onChange={(e) => setDiasDim(e.target.value)} className="text-xs bg-app border border-border rounded-md px-2 py-1">
            <option value="cliente">Por cliente</option><option value="rtc">Por RTC</option><option value="region">Por región</option>
          </select>
        </div>
        <div className="overflow-x-auto max-h-60">
          <table className="w-full text-xs">
            <thead className="bg-sidebar/60 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-2">{diasDim}</th><th className="p-2 text-right">Días promedio</th><th className="p-2 text-right"># Pagos</th><th className="p-2 text-right">Monto pagado</th>
              </tr>
            </thead>
            <tbody>
              {(diasPago?.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-border/30">
                  <td className="p-2">{r.fila}</td>
                  <td className={`p-2 text-right font-medium ${r.dias_promedio <= 15 ? "text-agro-green" : r.dias_promedio <= 30 ? "text-agro-yellow" : "text-red-400"}`}>{r.dias_promedio}d</td>
                  <td className="p-2 text-right">{r.pagos}</td>
                  <td className="p-2 text-right">{fmtL(r.monto_pagado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{label}</label>
    {children}
  </div>);
}


function downloadCSV(filename, headers, rows) {
  const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
